import { Box, Button, theme } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Sidepanel from 'modules/core/components/Sidepanel'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import legalEntities from 'modules/core/store/collections/legalEntities'

import BillingInfoSection from 'modules/billing/components/PaymentGateway/BillingInfoSection'
import F25SummaryCard from 'modules/billing/components/PaymentGateway/F25SummaryCard'
import PaymentInfoSection from 'modules/billing/components/PaymentGateway/PaymentInfoSection'
import { getChargebeeSubscriptionData } from 'modules/billing/components/PaymentGateway/PaymentInfoSection/helpers'
import SummaryCard from 'modules/billing/components/PaymentGateway/SummaryCard'
import { Step } from 'modules/billing/components/PaymentGateway/types'
import useAutoCompleteBillingInfo from 'modules/billing/hooks/useAutoCompleteBillingInfo'
import { SimpleChargebeeSubscription, getPlainSelectedPlan } from 'modules/billing/lib/prices'
import chargebeeSubscriptions from 'modules/billing/store/collections/chargebeeSubscriptions'

import { ChargebeeSubscriptionForm, ChargebeeSubscriptionFormFirstStep } from './form'

interface Props {
  planConfig: SimpleChargebeeSubscription[]
  setStep: (step: Step) => void
  isF25Pricing: boolean
}

const FullScreenStep = ({ planConfig, setStep, isF25Pricing }: Props) => {
  const { navigation } = useNavigation()
  const {
    access: { locale },
  } = useCurrentSession()
  const { data: firstStepFields, isAutoCompleted, writeData } = useAutoCompleteBillingInfo()
  const mainLegalEntity = legalEntities.main

  const form = useLazyRef(
    () =>
      new ChargebeeSubscriptionForm({
        ...(firstStepFields as ChargebeeSubscriptionFormFirstStep),
        country: mainLegalEntity?.attributes?.country,
        payment_method: 'card',
        card_number: null,
        card_expiry: null,
        card_cvv: null,
        iban: null,
        vat_has_been_validated: false,
        vat_validation: null,
      })
  ).current

  const upgradePlanEvent = () => {
    track(trackingEvents.billing.upgradePlanAddBillingPaymentInfo, {
      selected_plans: getPlainSelectedPlan(planConfig),
      is_auto_complete: isAutoCompleted,
    })
  }

  const { running: submitting, performAction: handleSubmit } = useAsyncAction(
    async () => {
      upgradePlanEvent()
      const chargebeeSubscriptionData = getChargebeeSubscriptionData({
        form,
        planConfig,
        locale,
      })
      await chargebeeSubscriptions.create(chargebeeSubscriptionData)
      writeData(form.firstStepValues)
      window.location.href = navigation.upgradePlan.success.fullPath()
    },
    {
      successFlash: false,
    }
  )

  return (
    <Sidepanel
      boxPadding={false}
      headerBorder={false}
      onClose={() => {
        setStep('close-reason')
        writeData(form.firstStepValues)
      }}
      open
      width="full"
    >
      <Box
        background="grey200"
        flexDirection="row"
        height="fit-content"
        minHeight="full"
        flexWrap="wrap"
        gap="s12"
        padding="s16"
      >
        <Box alignSelf="center" minWidth="s300" style={{ flex: '1' }}>
          {isF25Pricing ? <F25SummaryCard /> : <SummaryCard />}
        </Box>
        <Box
          background="grey000White"
          borderRadius={{
            all: 'abs008',
          }}
          boxShadow="s50"
          minWidth="s300"
          position="relative"
          maxHeight="full"
          style={{ flex: '1' }}
        >
          <Box paddingBottom="s80" paddingX="s16" style={{ paddingTop: '10%', overflowY: 'auto' }}>
            <Box alignSelf="center" maxWidth="s400" gap="s48">
              <PaymentInfoSection form={form} />
              <BillingInfoSection form={form} />
            </Box>
          </Box>
          <Box
            background="grey100"
            borderRadius={{
              bottomLeft: 'abs008',
              bottomRight: 'abs008',
            }}
            border={{ top: { color: 'darker160', style: 'solid', width: 's1' } }}
            paddingX="s24"
            paddingY="s16"
            alignItems="flexEnd"
            position="absolute"
            width="full"
            style={{ bottom: '0', zIndex: theme.zIndex.sidebar }}
          >
            <Button
              type="primary"
              disabled={submitting || !form.isPaymentDetailsValid || !form.isBillingInfoValid}
              onClick={handleSubmit}
            >
              {i18n.t('pricing_plans.billing_information.form.subscribe')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Sidepanel>
  )
}

export default observer(FullScreenStep)
